import React from "react";
import get from "lodash/get";
import Toast from "../parts/toast";
import cx from "classnames";

import iOSMajorVersion from "../utils/ios-major-version";

iOSMajorVersion();

import { GOOGLE_TAG_MANAGER } from "../constants";
import checkMenuExtended from "../utils/checkMenuExtended";
import ErrorBoundary from "../views/error-boundary";
import { browserEnv } from "../utils/browser";

class Page extends React.Component {
  constructor(props, ctx) {
    super(props, ctx);
    this.state = {
      style: undefined,
      isSafari: false,
      theme: this.context?.locals?.activeUser?.webAppTheme || "",
    };
    this.handleNavToggle = this.handleNavToggle.bind(this);
  }

  handleNavToggle({ target: { checked } }) {
    this.setState({ style: checked ? { overflow: "hidden" } : undefined });
  }

  componentDidUpdate(prevProps) {
    if (this.props !== prevProps && this.state.style) {
      this.setState({ style: undefined });
    }
  }

  componentDidMount() {
    this.setState({
      isSafari: !(
        window.navigator.userAgent.indexOf("Chrome") > -1 &&
        window.navigator.userAgent.indexOf("Safari") > -1
      ),
      theme:
        this.context?.locals?.activeUser?.webAppTheme ||
        (window?.matchMedia("(prefers-color-scheme: light)").matches
          ? "light"
          : "dark"),
    });

    // Handle close nav.
    const navToggle = document.getElementById("mobile-nav-toggle");
    if (navToggle) navToggle.addEventListener("change", this.handleNavToggle);

    this.loadExternalScripts();

    setTimeout(() => {
      if (process.env.NODE_ENV) {
        // load intercom
        if (this.context?.locals?.pathname?.includes("/chat")) {
          // no intercom on chat page
          return;
        }
        window.intercomSettings = { app_id: "tyo3o3a8" };
        this.initializeIntercom();
      }
    }, 0);

    if (window.env)
      (async function loadNewRelicAgent() {
        const { BrowserAgent } = await import(
          "@newrelic/browser-agent/loaders/browser-agent"
        );

        new BrowserAgent({
          init: {
            distributed_tracing: { enabled: true },
            privacy: { cookies_enabled: true },
            ajax: { deny_list: [window.env.REACT_APP_NEW_RELIC_BEACON] },
          },
          loader_config: {
            accountID: window.env.REACT_APP_NEW_RELIC_ACCOUNT_ID,
            trustKey: window.env.REACT_APP_NEW_RELIC_ACCOUNT_ID,
            agentID: window.env.REACT_APP_NEW_RELIC_APPLICATION_ID,
            licenseKey: window.env.REACT_APP_NEW_RELIC_LICENSE_KEY,
            applicationID: window.env.REACT_APP_NEW_RELIC_APPLICATION_ID,
          },
          info: {
            beacon: window.env.REACT_APP_NEW_RELIC_BEACON,
            errorBeacon: window.env.REACT_APP_NEW_RELIC_BEACON,
            licenseKey: window.env.REACT_APP_NEW_RELIC_LICENSE_KEY,
            applicationID: window.env.REACT_APP_NEW_RELIC_APPLICATION_ID,
            sa: 1,
          },
        });
      })();
  }

  componentWillUnmount() {
    const navToggle = document.getElementById("mobile-nav-toggle");
    if (!navToggle) return;
    navToggle.removeEventListener("change", this.handleNavToggle);
  }

  loadExternalScripts() {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        this.loadGoogleTagManager("dataLayer");
        this.loadFacebookPixel();
      });

      return;
    }

    setTimeout(() => {
      this.loadGoogleTagManager("dataLayer");
      this.loadFacebookPixel();
    }, 0);
  }

  // Start google tag manager.
  loadGoogleTagManager(dataLayer) {
    window[dataLayer] = window[dataLayer] || [];
    window[dataLayer].push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js",
    });

    const documentRef = document;
    const firstScriptRef = documentRef.getElementsByTagName("script")[0];
    const newScriptRef = documentRef.createElement("script");

    const formattedDataLayer =
      dataLayer !== "dataLayer" ? `&l=${dataLayer}` : "";

    newScriptRef.async = true;
    newScriptRef.src = `//www.googletagmanager.com/gtm.js?id=${GOOGLE_TAG_MANAGER}${formattedDataLayer}`;
    firstScriptRef.parentNode.insertBefore(newScriptRef, firstScriptRef);
  }

  // Start facebook pixel code.
  loadFacebookPixel() {
    const facebookPixelId = "408115669519688";
    const facebookPixelUrl = "https://connect.facebook.net/en_US/fbevents.js";

    const initializePixel = () => {
      if (window.fbq) {
        return;
      }

      const fbq = (...args) => {
        fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args);
      };

      window.fbq = fbq;

      if (!window._fbq) {
        window._fbq = fbq;
      }

      fbq.push = fbq;
      fbq.loaded = true;
      fbq.version = "2.0";
      fbq.queue = [];
    };

    const documentRef = document;

    const loadPixelScript = (url) => {
      const newScriptRef = documentRef.createElement("script");
      const firstScriptRef = documentRef.getElementsByTagName("script")[0];

      newScriptRef.async = true;
      newScriptRef.src = url;
      firstScriptRef.parentNode.insertBefore(newScriptRef, firstScriptRef);
    };

    initializePixel();
    loadPixelScript(facebookPixelUrl);

    window.fbq("init", facebookPixelId);
    window.fbq("track", "PageView");
  }

  initializeIntercom() {
    const windowIntercom = window.Intercom;

    // Check if Intercom is already initialized
    if (typeof windowIntercom === "function") {
      windowIntercom("reattach_activator");
      windowIntercom("update", intercomSettings);
      return;
    }

    // If not, initialize Intercom function and queue
    function initIntercom() {
      window.Intercom = (...args) => {
        window.Intercom.q.push(args);
      };
      window.Intercom.q = [];
    }

    // Load Intercom widget script
    function loadIntercomWidget() {
      const documentRef = document;
      const newScriptRef = documentRef.createElement("script");
      newScriptRef.type = "text/javascript";
      newScriptRef.async = true;
      newScriptRef.src = "https://widget.intercom.io/widget/tyo3o3a8";

      const firstScriptRef = documentRef.getElementsByTagName("script")[0];
      firstScriptRef.parentNode.insertBefore(newScriptRef, firstScriptRef);
    }

    // Initialize Intercom and load widget script
    initIntercom();

    // Load the widget script based on document state
    if (document.readyState === "complete") {
      loadIntercomWidget();
    } else if (window.attachEvent) {
      window.attachEvent("onload", loadIntercomWidget);
    } else {
      window.addEventListener("load", loadIntercomWidget, false);
    }
  }

  render() {
    const {
      props,
      context: { locals },
    } = this;

    if (
      locals.activeUser?.webAppTheme &&
      locals.activeUser.webAppTheme !== this.state.theme
    ) {
      this.setState({ theme: locals.activeUser.webAppTheme });
    }
    const theme = this.state.theme;
    const successMessage = locals.flash("success");
    const errorMessage = locals.flash("error");
    const warningMessage = locals.flash("warning");
    const informationMessage = locals.flash("information");

    const type = errorMessage
      ? "error"
      : successMessage
      ? "success"
      : warningMessage
      ? "warning"
      : informationMessage
      ? "information"
      : undefined;

    // this is used to determine if the header should be hidden. This is used by the mobile app that doesn't need the web app header
    // as it has it's own.
    let hideHeader = false;

    // check if we are in the browser or this is a load in the server with hideHeader=true set in the query
    if (process.browser || get(locals.query, "hideHeader", false)) {
      if (get(locals.query, "hideHeader", false)) {
        // if it's set and is set to true then hide the header
        hideHeader = true;
      } else {
        // if it is not set as a query param then default back to the window.hideHeader property set by the init middleware
        hideHeader = window.hideHeader;
      }
    }

    const VERSION = locals.appVersion;
    const isMenuExtended = checkMenuExtended(this.context.locals.pathname);
    return (
      <html lang="en" data-theme={theme}>
        <head>
          <meta charSet="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0"
          />
          <title>
            {locals.title === "MyStrengthBook - "
              ? locals.activeUser?.businessName
                ? `${locals.title} ${locals.activeUser?.businessName}`
                : `${locals.title} Track, Analize, Train`
              : locals.title}
          </title>
          <meta name="description" content={locals.description} />
          <meta property="og:url" content="https://app.mystrengthbook.com" />
          <meta property="og:type" content="product" />
          <meta property="og:title" content={locals.title} />
          <meta property="og:description" content={locals.description} />
          <meta
            property="og:image"
            content="https://app.mystrengthbook.com/img/MSB-logo-200.webp"
          />

          <link rel="shortcut icon" href="/img/favicon/favicon.png" />

          <link rel="preconnect" href="https://fonts.googleapis.com" />
          <link
            rel="preconnect"
            href="https://fonts.gstatic.com"
            crossOrigin="true"
          />
          <link
            href="https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,400..700;1,14..32,400..700&family=Open+Sans:ital,wght@0,400;0,600;0,800;1,400;1,600;1,800&display=swap"
            rel="stylesheet"
          />
          <link
            rel="stylesheet"
            href="https://unpkg.com/react-select@1.2.1/dist/react-select.css"
          />
          <link rel="stylesheet" href={`/css/app.css?v=${VERSION}`} />
        </head>
        <body
          style={(locals.modal && { overflow: "hidden" }) || this.state.style}
          className={
            iOSMajorVersion > 10 && locals.modal
              ? "ios-fix"
              : this.context.locals.pathname.indexOf("/metrics") > -1
              ? "is-metrics"
              : ""
          }
        >
          <input id="mobile-nav-toggle" type="checkbox" />
          <div
            id="page-wrapper"
            className={cx({
              "hide-header": hideHeader,
              "menu-extended": isMenuExtended,
              "safari-browser": this.state.isSafari,
            })}
          >
            <ErrorBoundary>{props.children}</ErrorBoundary>
          </div>
          <ErrorBoundary>{locals.modal}</ErrorBoundary>
          <Toast
            open={Boolean(type)}
            type={type}
            message={
              successMessage ||
              errorMessage ||
              warningMessage ||
              informationMessage
            }
          />
          <script async src="https://js.stripe.com/v3/" />
          <script
            defer
            dangerouslySetInnerHTML={{
              __html: browserEnv,
            }}
          />
          <script defer src={`/js/main.bundle.js?v=${VERSION}`} />
        </body>
      </html>
    );
  }
}

export default Page;
